import { IBusinessRule, IBusinessRuleItemSpecifier } from '../types/brandMenu';
import {
  BusinessRuleCategory,
  BusinessRuleOption,
} from '../constants/brandMenu';
import logger from './logger';
import { CartItem } from './cart';

export interface OptionReplacementInfo {
  shouldApplyReplacement: boolean;
  targetModifierGroups: Array<{
    modifierGroupName: string;
    optionName: string;
  }>;
}

/**
 * Extracts the base size from a combo size option (e.g., "small combo" -> "small")
 */
export const extractBaseSize = (sizeOption: string): string | null => {
  const suffixes = [' combo', ' side', ' drink', ' size'];
  for (const suffix of suffixes) {
    if (sizeOption.toLowerCase().endsWith(suffix)) {
      return sizeOption.slice(0, -suffix.length).trim();
    }
  }
  return null;
};

const normalizeName = (value: string): string => value.toLowerCase().trim();

/**
 * Generic function to match rule patterns (items or constraints) against candidates.
 * Supports wildcard matching with '*' pattern.
 * @param normalizeFn - Function to normalize strings for comparison
 */
export const matchesRulePattern = (
  patterns: Array<string | { name?: string | null } | null | undefined>,
  candidates: Array<string | null | undefined>,
  emptyPatternBehavior: 'allow' | 'deny',
  normalizeFn: (value: string) => string = normalizeName
): boolean => {
  if (!patterns || patterns.length === 0) {
    return emptyPatternBehavior === 'allow';
  }

  const normalizedCandidates = candidates
    .filter((candidate): candidate is string => !!candidate)
    .map((candidate) => normalizeFn(candidate));

  // Handle case where candidate is null/undefined - only match wildcard
  if (normalizedCandidates.length === 0) {
    return patterns.some((pattern) => {
      if (!pattern) {
        return false;
      }
      const patternValue = typeof pattern === 'string' ? pattern : pattern.name;
      return patternValue === '*';
    });
  }

  return patterns.some((pattern) => {
    if (!pattern) {
      return false;
    }

    const patternValue = typeof pattern === 'string' ? pattern : pattern.name;

    if (!patternValue) {
      return false;
    }

    if (patternValue === '*') {
      return true;
    }

    return normalizedCandidates.includes(normalizeFn(patternValue));
  });
};

const matchesRuleItems = (
  items: IBusinessRule['items_to_apply'],
  candidates: Array<string | null | undefined>
): boolean => {
  return matchesRulePattern(items, candidates, 'deny');
};

const hasWildcardItem = (items: IBusinessRule['items_to_apply']): boolean => {
  if (!items || items.length === 0) {
    return false;
  }

  return items.some(({ name }) => name === '*');
};

const matchesRuleConstraints = (
  constraints: IBusinessRule['constraints_to_apply'],
  candidate: string | null
): boolean => {
  return matchesRulePattern(constraints, [candidate], 'allow');
};

/**
 * Finds the selected combo size option name by matching against if.options from combo size transformation rules
 * Instead of using hardcoded COMBO_SIZE_MODIFIER_NAME_PREFIX, we use the rule's if.options to find matches
 * @param comboSizeTransformationRules - Pre-filtered combo size transformation rules
 * @returns The name of the selected combo size option, or undefined if not found
 */
const getSelectedComboSize = (
  cartItem: CartItem,
  comboSizeTransformationRules: IBusinessRule[]
): string | undefined => {
  if (comboSizeTransformationRules.length === 0) {
    logger.log(
      `getSelectedComboSize: No combo size transformation rules found for cart item "${cartItem.name}"`
    );
    return undefined;
  }

  // Collect all if.options from combo size transformation rules
  const comboSizeOptions = new Set<string>();
  comboSizeTransformationRules.forEach((rule) => {
    rule.if.options.forEach((option) => {
      if (option !== '*') {
        // Normalize and add to set
        comboSizeOptions.add(normalizeName(option));
      }
    });
  });

  if (comboSizeOptions.size === 0) {
    // If all rules use '*', we can't determine which options are combo sizes
    logger.log(
      `getSelectedComboSize: No combo size options found (all rules use '*') for cart item "${cartItem.name}"`
    );
    return undefined;
  }

  // Search through childModifierGroups for selected items that match combo size options
  // Only check childModifierGroups as that's where selected items are stored
  for (const childGroup of Object.values(cartItem.childModifierGroups || {})) {
    const selectedItems = Object.values(childGroup.selectedItems || {});

    for (const selectedItem of selectedItems) {
      const normalizedName = normalizeName(selectedItem.name);
      if (comboSizeOptions.has(normalizedName)) {
        return selectedItem.name;
      }
    }
  }

  return undefined;
};

/**
 * Finds a matching default size selection business rule
 * Matches rules based on if.options instead of constraints_to_apply
 */
const findMatchingDefaultSizeRule = (
  itemName: string,
  constraintName: string | null,
  selectedOption: string,
  defaultSizeRules: IBusinessRule[]
): IBusinessRule | null => {
  if (!defaultSizeRules || defaultSizeRules.length === 0) {
    return null;
  }

  return (
    defaultSizeRules.find((rule) => {
      // Only match default size selection rules
      if (
        rule.items_to_apply.length > 0 &&
        !matchesRuleItems(rule.items_to_apply, [itemName, selectedOption])
      ) {
        return false;
      }

      // Match rules based on if.options instead of constraints_to_apply
      // Check if the selected option matches any of the rule's if.options
      // "*" means match any option
      const normalizedSelectedOption = normalizeName(selectedOption);
      return rule.if.options.some(
        (option) =>
          option === '*' || normalizeName(option) === normalizedSelectedOption
      );
    }) || null
  );
};

/**
 * Finds a matching combo size transformation business rule
 * Matches rules based on if.options instead of constraints_to_apply
 * @param comboSizeTransformationRules - Pre-filtered combo size transformation rules
 */
const findMatchingComboSizeTransformationRule = (
  itemName: string,
  selectedOption: string,
  comboSizeTransformationRules: IBusinessRule[]
): IBusinessRule | null => {
  if (
    !comboSizeTransformationRules ||
    comboSizeTransformationRules.length === 0
  ) {
    return null;
  }

  return (
    comboSizeTransformationRules.find((rule) => {
      if (
        rule.items_to_apply.length > 0 &&
        !matchesRuleItems(rule.items_to_apply, [itemName])
      ) {
        return false;
      }

      // Match rules based on if.options instead of constraints_to_apply
      // Check if the selected option matches any of the rule's if.options
      // "*" means match any option
      const normalizedSelectedOption = normalizeName(selectedOption);
      return rule.if.options.some(
        (option) =>
          option === '*' || normalizeName(option) === normalizedSelectedOption
      );
    }) || null
  );
};

/**
 * Returns the first default-size rule that applies to a target modifier group,
 * taking into account the current selection context and child item state.
 */
const findDefaultRuleForTarget = (
  defaultSizeRules: IBusinessRule[],
  {
    targetName,
    hasExistingSelection,
    normalizedSelectedOptionName,
    selectedChildItems,
    contextModifierGroupName,
  }: {
    targetName: string;
    hasExistingSelection: boolean;
    normalizedSelectedOptionName: string | null;
    selectedChildItems: Array<{ name: string; modifierGroupName: string }>;
    contextModifierGroupName?: string | null;
  }
): IBusinessRule | undefined => {
  return defaultSizeRules.find((rule) => {
    if (!rule.then.target.includes(targetName)) {
      return false;
    }

    const ruleItems = rule.items_to_apply;
    const wildcardItem = hasWildcardItem(ruleItems);

    if (wildcardItem) {
      return !normalizedSelectedOptionName && !hasExistingSelection;
    }

    if (!ruleItems || ruleItems.length === 0) {
      return false;
    }

    const normalizedRuleItems = ruleItems
      .filter(
        (item): item is IBusinessRuleItemSpecifier =>
          !!item?.name && item.name !== '*'
      )
      .map((item) => normalizeName(item.name));

    if (normalizedSelectedOptionName) {
      const matchesSelectedOption = normalizedRuleItems.includes(
        normalizedSelectedOptionName
      );

      if (matchesSelectedOption) {
        const matchesIfOption = rule.if.options.some((option: string) => {
          if (option === '*') {
            return true;
          }
          return normalizeName(option) === normalizedSelectedOptionName;
        });

        if (matchesIfOption) {
          return true;
        }
      }
      // Fall through to evaluate selected child items if the chosen option
      // does not satisfy the rule's conditions.
    }

    return selectedChildItems.some((childItem) => {
      if (
        contextModifierGroupName &&
        normalizeName(childItem.modifierGroupName) ===
          normalizeName(contextModifierGroupName)
      ) {
        return false;
      }

      const normalizedChildName = normalizeName(childItem.name);
      return normalizedRuleItems.includes(normalizedChildName);
    });
  });
};

type ComboTargetState = {
  modifierGroup: CartItem['modifierGroups'][string];
  selectedItems: CartItem[];
  hasExistingSelection: boolean;
};

type DefaultSizeEvaluation = {
  defaultRule: IBusinessRule | undefined;
  enforceDefaultOptions: string[];
  shouldBlockCombo: boolean;
  preserveExistingSelection: boolean;
};

/**
 * Constructs the current combo target state, including the modifier definition
 * and any existing selections, if the modifier group is present on the cart item.
 */
const buildComboTargetState = (
  cartItem: CartItem,
  targetName: string
): ComboTargetState | null => {
  const modifierGroup = Object.values(cartItem.modifierGroups || {}).find(
    (group) => group.name === targetName
  );

  if (!modifierGroup) {
    logger.log(
      `Combo size transformation: Modifier group "${targetName}" not found on cart item "${cartItem.name}"`
    );
    return null;
  }

  const cartModifierGroup = modifierGroup.id
    ? cartItem.childModifierGroups?.[modifierGroup.id]
    : undefined;
  const selectedItems = Object.values(cartModifierGroup?.selectedItems || {});

  return {
    modifierGroup,
    selectedItems,
    hasExistingSelection: selectedItems.length > 0,
  };
};

/**
 * Evaluates the effect of default-size rules on a combo target, indicating whether
 * combo sizing should be blocked or allowed and if defaults should be enforced.
 */
const evaluateDefaultRuleForComboTarget = (
  targetName: string,
  state: ComboTargetState,
  defaultSizeRules: IBusinessRule[],
  options: {
    selectedContext?: {
      modifierGroupName: string;
      optionName: string;
    };
    selectedChildItems: Array<{ name: string; modifierGroupName: string }>;
  }
): DefaultSizeEvaluation => {
  const { selectedContext, selectedChildItems } = options;

  const isContextTarget =
    !!selectedContext &&
    normalizeName(selectedContext.modifierGroupName) ===
      normalizeName(targetName);

  const normalizedSelectedOptionName = selectedContext
    ? normalizeName(selectedContext.optionName)
    : null;

  const defaultRule = isContextTarget
    ? undefined
    : findDefaultRuleForTarget(defaultSizeRules, {
        targetName,
        hasExistingSelection: state.hasExistingSelection,
        normalizedSelectedOptionName,
        selectedChildItems,
        contextModifierGroupName: selectedContext?.modifierGroupName || null,
      });

  const enforceDefaultOptions =
    defaultRule && !state.hasExistingSelection
      ? [...defaultRule.then.target_options]
      : [];

  const defaultRuleOptions =
    defaultRule?.then.target_options.map((option) => option.toLowerCase()) ||
    [];

  const currentSelections =
    state.selectedItems.map((item) => item.name.toLowerCase()) || [];

  const preserveExistingSelection =
    !!defaultRule &&
    state.hasExistingSelection &&
    defaultRuleOptions.length > 0 &&
    currentSelections.length > 0 &&
    currentSelections.every((selection) =>
      defaultRuleOptions.includes(selection)
    );

  const shouldBlockCombo =
    !!defaultRule &&
    !isContextTarget &&
    (enforceDefaultOptions.length > 0 ||
      preserveExistingSelection ||
      defaultRule.then.target_options.length === 0);

  return {
    defaultRule,
    enforceDefaultOptions,
    shouldBlockCombo,
    preserveExistingSelection,
  };
};

/**
 * Produces combo size selections for all eligible targets by matching the base size
 * against menu options in each modifier group.
 */
const collectComboSizeTransformationTargets = (
  cartItem: CartItem,
  baseSize: string,
  transformationRule: IBusinessRule,
  eligibleTargets: Set<string>,
  targetStates: Record<string, ComboTargetState>
): Array<{ modifierGroupName: string; optionName: string }> => {
  const normalizedBaseSize = normalizeName(baseSize);
  const targetModifierGroups: Array<{
    modifierGroupName: string;
    optionName: string;
  }> = [];

  transformationRule.then.target.forEach((targetName) => {
    if (!eligibleTargets.has(targetName)) {
      return;
    }

    const state = targetStates[targetName];

    if (!state) {
      return;
    }

    const candidateItems = Object.values(state.modifierGroup.menuItems || {});

    if (!candidateItems.length) {
      logger.log(
        `Combo size transformation: No menu items found in modifier group "${targetName}" for cart item "${cartItem.name}"`
      );
      return;
    }

    const matchingItem =
      candidateItems.find((item) =>
        normalizeName(item.name).startsWith(`${normalizedBaseSize} `)
      ) ||
      candidateItems.find((item) =>
        normalizeName(item.name).includes(normalizedBaseSize)
      );

    if (!matchingItem) {
      logger.log(
        `Combo size transformation: No option containing size "${baseSize}" found in modifier group "${targetName}" for cart item "${cartItem.name}"`
      );
      return;
    }

    const alreadySelected = state.selectedItems.some(
      (item) => normalizeName(item.name) === normalizeName(matchingItem.name)
    );

    if (alreadySelected) {
      logger.log(
        `Combo size transformation: Target "${targetName}" already selected as "${matchingItem.name}", skipping`
      );
      return;
    }

    targetModifierGroups.push({
      modifierGroupName: targetName,
      optionName: matchingItem.name,
    });
  });

  return targetModifierGroups;
};

/**
 * Applies default-size rules first and then derives combo size selections for a rule,
 * ensuring default requirements are respected before queuing combo transformations.
 */
const resolveComboSizeTransformationTargets = (
  cartItem: CartItem,
  baseSize: string,
  transformationRule: IBusinessRule,
  defaultSizeRules: IBusinessRule[],
  selectedContext?: {
    modifierGroupName: string;
    optionName: string;
  }
): Array<{ modifierGroupName: string; optionName: string }> => {
  const queuedTargetModifierGroups: Array<{
    modifierGroupName: string;
    optionName: string;
  }> = [];

  const targetStates: Record<string, ComboTargetState> = {};
  const eligibleTargets = new Set<string>();

  const selectedChildItems = Object.values(
    cartItem.childModifierGroups || {}
  ).flatMap((group) =>
    Object.values(group.selectedItems || {}).map((item) => ({
      name: item.name,
      modifierGroupName: group.name,
    }))
  );

  transformationRule.then.target.forEach((targetName) => {
    const state = buildComboTargetState(cartItem, targetName);

    if (!state) {
      return;
    }

    targetStates[targetName] = state;

    const defaultEvaluation = evaluateDefaultRuleForComboTarget(
      targetName,
      state,
      defaultSizeRules,
      {
        selectedContext,
        selectedChildItems,
      }
    );

    if (defaultEvaluation.enforceDefaultOptions.length > 0) {
      defaultEvaluation.enforceDefaultOptions.forEach((optionName) => {
        const alreadyQueued = queuedTargetModifierGroups.some(
          (queued) =>
            queued.modifierGroupName === targetName &&
            queued.optionName === optionName
        );

        if (!alreadyQueued) {
          queuedTargetModifierGroups.push({
            modifierGroupName: targetName,
            optionName,
          });
        }
      });

      if (defaultEvaluation.defaultRule) {
        logger.log(
          `Combo size transformation: Ensured default size selection via rule "${defaultEvaluation.defaultRule.name}" for modifier group "${targetName}"`
        );
      }
    }

    if (defaultEvaluation.shouldBlockCombo) {
      if (defaultEvaluation.preserveExistingSelection) {
        logger.log(
          `Combo size transformation: Preserving existing default selection for modifier group "${targetName}"`
        );
      }
      return;
    }

    eligibleTargets.add(targetName);
  });

  const comboTargets =
    eligibleTargets.size > 0
      ? collectComboSizeTransformationTargets(
          cartItem,
          baseSize,
          transformationRule,
          eligibleTargets,
          targetStates
        )
      : [];

  return [...queuedTargetModifierGroups, ...comboTargets];
};

/**
 * Analyze if option replacement should be applied based on modifier selection
 * Uses business rules from the menu JSON exclusively (no config fallback)
 */
export const analyzeOptionReplacement = (
  modifierGroupName: string,
  selectedMenuItemName: string,
  cartItem: CartItem,
  businessRules: IBusinessRule[]
): OptionReplacementInfo => {
  if (!businessRules || businessRules.length === 0) {
    return { shouldApplyReplacement: false, targetModifierGroups: [] };
  }

  const defaultSizeRules = businessRules.filter(
    (rule) =>
      rule.category === BusinessRuleCategory.DefaultSizeSelection &&
      rule.then.operation === BusinessRuleOption.SelectDefaultSize
  );

  // Match rules based on if.options instead of constraints_to_apply
  const matchingRule = findMatchingDefaultSizeRule(
    cartItem.name,
    null, // constraintName no longer used for matching
    selectedMenuItemName,
    defaultSizeRules
  );

  if (matchingRule) {
    const targets = matchingRule.then.target;
    logger.log(
      `Option replacement triggered via business rule: ${selectedMenuItemName} -> ${targets.join(
        ', '
      )}`
    );

    const targetModifierGroups: Array<{
      modifierGroupName: string;
      optionName: string;
    }> = [];

    if (matchingRule.then.target_options.length > 0) {
      matchingRule.then.target_options.forEach((targetOption) => {
        targets.forEach((target) => {
          targetModifierGroups.push({
            modifierGroupName: target,
            optionName: targetOption,
          });
        });
      });
    }

    if (targetModifierGroups.length > 0) {
      logger.log(
        `Option replacement triggered: ${selectedMenuItemName} -> applying to ${targetModifierGroups.length} modifier groups`
      );
      return {
        shouldApplyReplacement: true,
        targetModifierGroups,
      };
    }

    return { shouldApplyReplacement: false, targetModifierGroups: [] };
  }

  // Get combo size transformation rules to find selected combo size
  const comboSizeTransformationRules = businessRules.filter(
    (rule) =>
      rule.category === BusinessRuleCategory.ComboSizeTransformation &&
      rule.then.operation === BusinessRuleOption.TransformComboSize
  );

  // Check if the selected option matches any combo size transformation rule's if.options
  const isSelectedOptionComboSize = comboSizeTransformationRules.some(
    (rule) => {
      const normalizedSelectedOption = normalizeName(selectedMenuItemName);
      return rule.if.options.some(
        (option) =>
          option === '*' || normalizeName(option) === normalizedSelectedOption
      );
    }
  );

  // Get the effective combo size name
  // If the selected option is a combo size, use it directly; otherwise, dont use combo size transformation values
  const effectiveComboSizeName = isSelectedOptionComboSize
    ? selectedMenuItemName
    : false;

  if (!effectiveComboSizeName) {
    logger.log(
      `No combo size selected; skipping combo transformation. Item: ${cartItem.name}, Constraint: ${modifierGroupName}, Option: ${selectedMenuItemName}`
    );
    return { shouldApplyReplacement: false, targetModifierGroups: [] };
  }

  // Try to find matching combo size transformation rule using the effective combo size name
  // Reuse the already-filtered comboSizeTransformationRules
  const comboSizeTransformationRule = findMatchingComboSizeTransformationRule(
    cartItem.name,
    effectiveComboSizeName,
    comboSizeTransformationRules
  );

  if (!comboSizeTransformationRule) {
    logger.log(
      `No combo size transformation rule found. Item: ${cartItem.name}, Constraint: ${modifierGroupName}, Option: ${selectedMenuItemName}`
    );
    return { shouldApplyReplacement: false, targetModifierGroups: [] };
  }

  const baseSize = extractBaseSize(effectiveComboSizeName);

  if (!baseSize) {
    logger.log(
      `Could not extract base size from option "${modifierGroupName}" -> "${selectedMenuItemName}" using effective combo size "${effectiveComboSizeName}"`
    );
    return { shouldApplyReplacement: false, targetModifierGroups: [] };
  }

  const targetModifierGroups = resolveComboSizeTransformationTargets(
    cartItem,
    baseSize,
    comboSizeTransformationRule,
    defaultSizeRules,
    isSelectedOptionComboSize
      ? undefined
      : {
          modifierGroupName,
          optionName: selectedMenuItemName,
        }
  );

  if (targetModifierGroups.length > 0) {
    logger.log(
      `Combo size transformation applied for "${selectedMenuItemName}" (combo size "${effectiveComboSizeName}") targeting ${targetModifierGroups.length} modifier groups`
    );
    return {
      shouldApplyReplacement: true,
      targetModifierGroups,
    };
  }

  logger.log(
    `Combo size transformation rule "${comboSizeTransformationRule.name}" produced no targets for item "${cartItem.name}" and base size "${baseSize}"`
  );
  return { shouldApplyReplacement: false, targetModifierGroups: [] };
};
